/*
 *  Interface représentant un devis tel qu'utilisé dans l'application React et l'application API
 *  Contient également l'interface pour les indices de devis
 *  Ainsi que l'interface pour les lignes de devis.
 */

import { ServiceData, TaxRateData } from '../../services';
import { ProductData } from '../../products';
import { TiersData, TiersTypeData } from '../../tiers';
import type { StatusConfig } from '../../../common/components/StatusCard/StatusCard';
import {
	faCheckCircle,
	faFileAlt,
	faPaperPlane,
	faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { DocumentData } from 'features/documents/types/types';
import { UserData } from 'features/users/types/types';

export const signatoryOptions = [
	{
		key: 'F.ROUCAYROL ADMINISTRATEUR EDF',
		label: 'F.ROUCAYROL ADMINISTRATEUR EDF',
	},
	{
		key: 'P.RISTORCELLI ADMINISTRATEUR FRAMATOME',
		label: 'P.RISTORCELLI ADMINISTRATEUR FRAMATOME',
	},
	{
		key: 'F. ROUCAYROL et P. RISTORCELLI les Administrateurs du GIE CETIC',
		label: 'F. ROUCAYROL et P. RISTORCELLI les Administrateurs du GIE CETIC',
	},
];

export interface QuoteUserData {
	firstName: string;
	name: string;
}

export interface DocumentTemplateData {
	id: number;
	code: string;
	label: string;
	htmlContent: string;
	cssContent?: string;
	type: string;
}

export interface QuoteClassificationData {
	id: number;
	code: string;
	label: string;
	parent?: QuoteClassificationData | null;
}

export interface QuoteLineData {
	// Obligatoires
	id: number;
	description: string;
	// Champs simples
	unitPrice: number;
	quantity: number;
	total: number;
	nbDays?: number;
	nbParticipants?: number;
	manualOverride?: boolean;
	subcontracted?: boolean;
	// Relations
	service?: ServiceData | null;
	product?: ProductData | null;
	taxRate: TaxRateData;
	quoteVersion: QuoteVersionData;
}

export interface QuoteVersionData {
	// Obligatoires
	id: number;
	label: string;
	// Champs simples
	status?: string;
	/** Motif saisi pour valider cette version sans commande client reçue */
	validationWithoutOrderReason?: string | null;
	versionIndex: number;
	commentIndex?: string;
	editedAt?: string;
	startDate?: string;
	endDate?: string;
	activityStartDate?: string;
	activityEndDate?: string;
	fiscalYear?: number;
	tiersReference?: string;
	publicNotes?: string;
	generatedText?: string;
	contextNotes?: string;
	signatory?: string;
	relatedApproval?: boolean;
	// Relations
	quoteLines: QuoteLineData[];
	documentTemplate?: DocumentTemplateData;
	quote: QuoteData;
	businessManager?: {
		id: number;
		firstName: string;
		lastName: string;
		email?: string;
		workPhone?: string;
		mobilePhone?: string;
	} | null;
	document?: DocumentData;
	assignedUser?: UserData | null;
	relatedTiers?: TiersData;
	// Métadonnées
	createdAt?: string;
	createdBy?: UserData;
}

export interface QuoteData {
	// Obligatoires
	id: number;
	referenceNumber: string;
	// Champs simples
	budgeted?: boolean;
	// Relations
	tiers?: {
		id: number;
		label: string;
		contacts?: {
			id: number;
			firstName: string;
			lastName: string;
			workPhone?: string;
			mobilePhone?: string;
			email?: string;
			position?: string;
			businessManager?: boolean;
			status: boolean;
		}[];
		addresses?: {
			id: number;
			types: {
				code: string;
			}[];
			label: string;
			line1: string;
			line2?: string;
			zipCode: string;
			city: string;
			country: string;
			status: boolean;
		}[];
	};
	classification?: QuoteClassificationData;
	quoteVersions?: QuoteVersionData[];
	/** Dernière version active du devis, retournée directement par l'API */
	latestActiveQuoteVersion?: {
		id: number;
		versionIndex: number;
		commentIndex?: string;
		totalExclTax?: number;
		startDate?: string;
		endDate?: string;
		activityStartDate?: string;
		activityEndDate?: string;
		createdAt?: string;
		editedAt?: string;
		status?: string;
		tiersReference?: string;
		fiscalYear?: number;
		signatory?: string;
		assignedUser?: { id: number; name: string; firstName?: string };
		businessManager?: { id: number; firstName?: string; lastName: string };
	};
	tiersType?: TiersTypeData;
	project?: {
		id: number;
		referenceNumber: string;
	} | null;
	// Métadonnées
	creator?: QuoteUserData;
	createdAt?: string;
}

export const QUOTE_STATUS_CONFIG: StatusConfig = {
	steps: [
		{
			key: 'CANCELED',
			label: 'Annulé',
			icon: faTimesCircle,
			color: 'bg-red-600 text-white',
			textColor: 'text-red-600',
			side: true,
		},
		{
			key: 'DRAFT',
			label: 'Brouillon',
			icon: faFileAlt,
			color: 'bg-blue-600 text-white',
			textColor: 'text-blue-600',
		},
		{
			key: 'SENT',
			label: 'Envoyé',
			icon: faPaperPlane,
			color: 'bg-purple-600 text-white',
			textColor: 'text-purple-600',
		},
		{
			key: 'VALIDATE',
			label: 'Validé',
			icon: faCheckCircle,
			color: 'bg-green-600 text-white',
			textColor: 'text-green-600',
		},
	],
	transitions: {
		CANCELED: [
			{
				to: 'DRAFT',
				label: 'Brouillon',
				type: 'main' as const,
			},
		],
		DRAFT: [
			{ to: 'CANCELED', label: 'Annuler', type: 'secondary' as const },
			{
				to: 'SENT',
				label: 'Envoyer',
				type: 'main',
				alert: 'Êtes-vous sûr de vouloir envoyer ce devis ?' as const,
			},
		],
		SENT: [
			{
				to: 'CANCELED',
				label: 'Annuler',
				type: 'secondary',
				alert: 'Êtes-vous sûr de vouloir annuler ce devis ?' as const,
			},
			{
				to: 'VALIDATE',
				label: 'Valider',
				type: 'main',
				alert: 'Êtes-vous sûr de vouloir valider ce devis ?' as const,
			},
		],
		VALIDATE: [
			{
				to: 'CANCELED',
				label: 'Annuler',
				type: 'secondary',
				alert: 'Êtes-vous sûr de vouloir annuler ce devis ?' as const,
			},
		],
	},
};
